import type React from 'react'
import { useImperativeHandle } from 'react'
import type { CropOverlayRef } from '../types/crop-overlay-props'
import type { CropDragState } from './use-crop-drag-state'

/** Exposes `cancelActiveDrag` (see `CropOverlayRef`) on the overlay's forwarded ref. */
export function useCropCancelDrag(ref: React.ForwardedRef<CropOverlayRef>, state: CropDragState) {
  const { dragRef, forceUpdate, geomRef, photoFillRef } = state

  useImperativeHandle(
    ref,
    () => ({
      cancelActiveDrag: () => {
        const drag = dragRef.current

        if (!drag) return false

        if (drag.type === 'resize') {
          geomRef.current = drag.initialGeom
          photoFillRef.current = drag.initialPhotoFill
        } else if (drag.type === 'pan') {
          // No full initialPhotoFill snapshot for a pan (see PanDrag) — only offsetX/offsetY
          // ever change during a pan, so reverting just those two fields off the current
          // photoFill is exact.
          if (photoFillRef.current) {
            photoFillRef.current = {
              ...photoFillRef.current,
              offsetX: drag.initialOffsetX,
              offsetY: drag.initialOffsetY,
            }
          }
        } else if (drag.type === 'rotate') {
          photoFillRef.current = drag.initialPhotoFill
        }
        // 'shapeRotate' has no local ref to revert — it only ever mutates `ShapeData.rotation`
        // on the real shape via onShapeRotateChange, which the caller reverts through
        // `cropGestureBeforeRef`; dropping the drag below is all this overlay needs to do.

        dragRef.current = null
        forceUpdate((n) => n + 1)

        return true
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
}
